import 'bootstrap/dist/css/bootstrap.min.css';
import { useEffect, useState } from 'react';
import 'antd/dist/antd.less';
import './App.css';
import './components/dashboard/dashboard.css';
import AuhenticationContext from './contexts/authentication.context';
import authService from './services/auth.service';
import { AppRouter } from './containers';
import { GlobalStyles } from './global-styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { availableThemes } from './themes/common';
import { ThemeProvider } from 'styled-components';

const themeMapper = {
	'psut': availableThemes.psutColors,
	'talents-arena': availableThemes.defaultTAColors
};

const App = () => {
	const token = authService.getAccessToken();
	const authenticated = authService.isAuthenticated(token);

	const [authState, setAuthState] = useState({
		authenticated,
		token,
		activated: authService.isActivated(),
		user: authenticated ? authService.getUserData() : null,
		tempAccess: false,
		backOffice:
			authenticated && authService.hasRoles('back_office_super_admin', 'back_office_admin', 'back_office_user')
	});

	//////////////THEME CHANGING/////////////////////
	const [theme, setTheme] = useState(availableThemes.defaultTAColors); //default theme
	const selectedTheme = authService.getUserData()?.company?.tenant;

	useEffect(() => {
		if (selectedTheme) {
			setTheme(themeMapper[selectedTheme] ?? availableThemes.defaultTAColors);
		} else {
			setTheme(availableThemes.defaultTAColors);
		}
		localStorage.setItem('theme', theme.title);
	}, [theme.title, selectedTheme]);
	/////////////THEME CHANGING/////////////////////

	useEffect(() => {
		//Changes the fav icon based on the theme
		let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
		if (!link) {
			link = document.createElement('link');
			link.rel = 'icon';
			document.getElementsByTagName('head')[0].appendChild(link);
		}
		link.href = theme.value.FAV_ICON;
		document.title = theme.value.TAB_TITLE ?? 'Talents Arena';
	}, [theme.title]);

	const queryClient = new QueryClient();

	const mountLogic = async () => {
		let authenticated = false;
		const decodedToken = await authService.getDecodedToken();

		try {
			authenticated = await authService.verifyToken();
		} catch (e) {
			console.error(e);
		}
		setAuthState({
			...authState,
			authenticated,
			activated: authService.isActivated(),
			tempAccess: decodedToken?.companyTempAccess
		});

		if (!authenticated) {
			localStorage.removeItem('profile');
			localStorage.removeItem('access_token');
		} else {
			addIntercom(authState.user);
		}
		if (process.env.REACT_APP_ENV === 'production') {
			addHotJar();
			addGoogleAnalytics();
		}
	};

	useEffect(() => {
		mountLogic();
	}, []);

	const addHotJar = () => {
		let script = document.createElement('script');
		script.innerHTML = `(function (h, o, t, j, a, r) {
				h.hj =
					h.hj ||
					function () {
						(h.hj.q = h.hj.q || []).push(arguments);
					};
				h._hjSettings = { hjid: 2024447, hjsv: 6 };
				a = o.getElementsByTagName('head')[0];
				r = o.createElement('script');
				r.async = 1;
				r.src = t + h._hjSettings.hjid + j + h._hjSettings.hjsv;
				a.appendChild(r);
			})(window, document, 'https://static.hotjar.com/c/hotjar-', '.js?sv=');`;
		script.type = 'text/javascript';
		document.body.appendChild(script);
	};

	const addGoogleAnalytics = () => {
		const script1 = document.createElement('script');
		script1.type = 'text/javascript';

		script1.async = true;
		script1.src = 'https://www.googletagmanager.com/gtag/js?id=G-QQK4S8G5CF';

		const script2 = document.createElement('script');
		script2.innerHTML = `
			window.dataLayer = window.dataLayer || [];
			function gtag() {
				dataLayer.push(arguments);
			}
			gtag('js', new Date());

			gtag('config', 'G-QQK4S8G5CF');`;
		script2.type = 'text/javascript';
		document.head.prepend(script2);
		document.head.prepend(script1);
	};

	const addIntercom = user => {
		const initScript = document.createElement('script');
		initScript.type = 'text/javascript';

		initScript.text = `
		window.intercomSettings = {
			app_id: "ycjad9sm",
			name: "${user.firstName} ${user.lastName}", // Full name
			email: "${user.email}", // Email address
			created_at: "${new Date(user.createdAt).getTime()}" // Signup date as a Unix timestamp
		};`;
		// document.body.appendChild(initScript);
		document.body.appendChild(initScript);
		const script = document.createElement('script');
		script.type = 'text/javascript';

		script.text = `
		// We pre-filled your app ID in the widget URL: 'https://widget.intercom.io/widget/ycjad9sm'
		(function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/ycjad9sm';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if (document.readyState=="complete"||document.readyState=="loaded"){l();}if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();`;

		document.body.appendChild(script);
	};

	const handleTabClose = event => {
		event.preventDefault();
		if (authState.tempAccess) return authService.logout();
	};

	useEffect(() => {
		window.addEventListener('beforeunload', handleTabClose);
		return () => {
			window.removeEventListener('beforeunload', handleTabClose);
		};
	}, [authState.tempAccess]);

	const setAuthenticated = (state, token = null, profile: any = null) => {
		if (token) authService.setAccessToken(token);
		const decodedToken = authService.getDecodedToken(token);
		if (profile) {
			profile = { ...profile, userId: decodedToken.sub };
			authService.setUserData(profile);
		}
		setAuthState({
			authenticated: state,
			token: token ?? authState.token,
			user: profile || authState.user,
			activated: token ? authService.isActivated() : authState.activated,
			tempAccess: !!decodedToken?.companyTempAccess,
			backOffice:
				authenticated &&
				authService.hasRoles('back_office_super_admin', 'back_office_admin', 'back_office_user')
		});
		addIntercom(profile);
	};

	const setUser = profile => setAuthState({ ...authState, user: profile });

	return (
		<>
			<ThemeProvider theme={theme.value}>
				<GlobalStyles />
				<QueryClientProvider client={queryClient}>
					<AuhenticationContext.Provider
						value={{
							authenticated: authState.authenticated,
							setAuthenticated,
							user: authState.user,
							setUser,
							tempAccess: authState.tempAccess
						}}
					>
						<AppRouter authState={authState} />
					</AuhenticationContext.Provider>
				</QueryClientProvider>
			</ThemeProvider>
		</>
	);
};

export default App;
